import { Fragment, useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  File,
  PlaneTakeoff,
  FileText,
  CircleMinus,
  TowerControl
} from '@/components/icons'
import { hapticLight } from '@/lib/haptics'
import { Button } from './ui/button'
import { Card } from '@/components/ui/card'
import { PrntrGroup, PrntrInput, PrntrValue } from '@/components/ui/prntr-input'
import { SectionTitle } from './ui/section-title'
import { SwipeableRow } from './ui/swipeable-row'
import { EmptyStateCard } from './EmptyStateCard'
import { TopBarBackIconButton } from './topbar/TopBarBackIconButton'
import { useBackTo } from './topbar/useBackTo'
import { useTripGetById, useTripUpdateComplianceDocuments, useNoteDelete, useAirportByIcao, useAirportsByIcaos, type PendingFlightLog } from '../lib/hooks'
import { useIsAdmin } from '../hooks/useIsCompanyAdmin'
import { useIsPilot } from '../hooks/useIsPilot'
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember'
import { FlightLogRingCard } from './FlightLogStatusCard'
import { FlightLogsAttentionDrawer } from './FlightLogsAttentionDrawer'
import { Capacitor } from '@capacitor/core'
import { FlightMap } from './FlightMap'
import { setBaseOverlayRect, clearBaseOverlayRect } from '@/lib/native/flight-map-overlay'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
} from './ui/alert-dialog'
import { NotesSection } from './NotesSection'
import { AirportDetailDrawer } from './AirportDetailDrawer'
import { FlightDrawer } from './FlightDrawer'
import { ViewPasswordsDrawer } from './ViewPasswordsDrawer'
import { TripActionsSelect } from './TripActionsSelect'
import { AddComplianceDocumentToTripDrawer } from './AddComplianceDocumentToTripDrawer'
import { TripAddDocumentDrawer } from './TripAddDocumentDrawer'
import { DocumentFormDrawer } from './documents/DocumentFormDrawer'
import { ViewDocumentDrawer } from './documents/ViewDocumentDrawer'
import { Skeleton } from '@/components/ui/skeleton'
import { TripFlightCarousel } from './TripFlightCarousel'
import { TripCard } from './TripCard'
import { TripStatusSelect } from './TripStatusSelect'
import { AppLayout } from './AppLayout'
import { DesktopPage } from './desktop/DesktopPage'
import { DesktopDetailHeader } from './desktop/DesktopDetailHeader'
import { useIsDesktop } from './ui/use-mobile'
import { Link } from '@tanstack/react-router'
import { useTripStatusActions } from '@/hooks/useTripStatusActions'
import { toLifecycleStatus, isTripEditable } from '@/lib/tripState'
import { isFlightLogOpenForLeg } from '@@api/flightLog.shared'

interface TripDetailViewProps {
  tripId: string
  editTripId?: string
  openLegId?: string
  readOnly?: boolean
  /**
   * Allow any company member (not just admins) to add documents to the trip.
   * Used on the crew-facing schedule route, where the trip is otherwise read-only
   * but uploading trip documents is permitted. Non-admins get the upload form
   * directly; admins still get the full "Add Documents" menu.
   */
  allowDocumentUpload?: boolean
}

function getTripStartDate(legs?: any[]): string | null {
  if (!legs || legs.length === 0) return null
  const sortedLegs = [...legs].sort(
    (a, b) =>
      new Date(a.departureDateTime).getTime() -
      new Date(b.departureDateTime).getTime()
  )
  return sortedLegs[0].departureDateTime
}

function getTripEndDate(legs?: any[]): string | null {
  if (!legs || legs.length === 0) return null
  const sortedLegs = [...legs].sort(
    (a, b) =>
      new Date(a.arrivalDateTime).getTime() -
      new Date(b.arrivalDateTime).getTime()
  )
  return sortedLegs[sortedLegs.length - 1].arrivalDateTime
}

export function TripDetailView({ tripId, editTripId, openLegId, readOnly = false, allowDocumentUpload = false }: TripDetailViewProps) {
  const navigate = useNavigate()
  const handleBack = useBackTo('/operations/trips')
  const isDesktop = useIsDesktop()
  const { isAdmin, isLoaded: isAdminLoaded } = useIsAdmin()
  const { isPilot } = useIsPilot()
  const { data: currentCrew } = useCurrentCrewMember()
  const [isFlightDrawerOpen, setIsFlightDrawerOpen] = useState(false)
  const [selectedFlightId, setSelectedFlightId] = useState<string | null>(null)
  // When true, the open leg's FlightMap shows as a full-screen backdrop behind
  // the drawer. Toggled by the drawer's globe button — it does NOT navigate.
  const [showMap, setShowMap] = useState(false)
  // Controlled snap point for the leg drawer: open at 0.9, collapse to 0.3 when
  // the map backdrop is shown so the map is visible.
  const [legSnap, setLegSnap] = useState<number | string | null>(0.9)
  const [isViewPasswordsDrawerOpen, setIsViewPasswordsDrawerOpen] =
    useState(false)
  const [isFlightLogsAttentionOpen, setIsFlightLogsAttentionOpen] = useState(false)
  const [isTripAddDocumentDrawerOpen, setIsTripAddDocumentDrawerOpen] = useState(false)
  const [isAddComplianceDocumentDrawerOpen, setIsAddComplianceDocumentDrawerOpen] = useState(false)
  const [isTripDocumentDrawerOpen, setIsTripDocumentDrawerOpen] = useState(false)
  const [isViewDocumentDrawerOpen, setIsViewDocumentDrawerOpen] = useState(false)
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)
  const [openRowId, setOpenRowId] = useState<string | null>(null)
  // Airports section is collapsed to the first 3 rows per design.
  const [showAllAirports, setShowAllAirports] = useState(false)
  // Airport whose info drawer is open (tapped in the Airports section).
  const [infoIcao, setInfoIcao] = useState<string | null>(null)
  // Item awaiting confirmation in the remove dialog.
  const [removeTarget, setRemoveTarget] = useState<
    | { type: 'document'; id: string; title: string }
    | { type: 'note'; id: string; title: string }
    | null
  >(null)
  // Auto-open leg drawer when navigated with openLegId
  useEffect(() => {
    if (openLegId) {
      setSelectedFlightId(openLegId)
      setIsFlightDrawerOpen(true)
      setLegSnap(0.9)
    }
  }, [openLegId])

  const {
    data: trip,
    isPending: loading,
    error,
  } = useTripGetById(tripId)

  // The leg currently open in the drawer — used to drive the FlightMap backdrop.
  const selectedLeg = trip?.legs?.find((l) => l.id === selectedFlightId)
  const mapDeparture = useAirportByIcao(selectedLeg?.departureAirportIcao)
  const mapArrival = useAirportByIcao(selectedLeg?.arrivalAirportIcao)

  // While the map backdrop is shown, register only the drawer's rect as the
  // interactive region so the rest of the map stays pannable/rotatable. The rect
  // is the bottom `legSnap` fraction of the viewport (where the drawer sits).
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') return
    if (!showMap || typeof legSnap !== 'number') return
    const vw = window.innerWidth
    const vh = window.innerHeight
    const height = vh * legSnap
    setBaseOverlayRect({ x: 0, y: vh - height, width: vw, height })
    return () => clearBaseOverlayRect()
  }, [showMap, legSnap])

  // Get compliance documents from trip
  const complianceDocuments = trip?.documents || []

  useEffect(() => {
    if (error) {
      console.error('Error fetching trip details:', error)
      toast.error('Failed to load trip details')
    }
  }, [error])

  useEffect(() => {
    if (!loading && !trip) {
      toast.error('Trip not found')
    }
  }, [loading, trip])

  // Unique crew across all legs with per-leg acceptance tallies
  // (TripLegCrew.acknowledged): assigned = legs on this trip, accepted =
  // legs acknowledged. Drives the Crew section badges and the card count.
  const allLegCrew = trip?.legs?.flatMap((leg) => leg.crew ?? []) ?? []
  const tripCrewById = new Map<
    string,
    { crew: (typeof allLegCrew)[number]; assigned: number; accepted: number }
  >()
  for (const crew of allLegCrew) {
    const entry = tripCrewById.get(crew.id) ?? { crew, assigned: 0, accepted: 0 }
    entry.assigned += 1
    if (crew.acknowledged) entry.accepted += 1
    tripCrewById.set(crew.id, entry)
  }
  const tripCrewSummary = [...tripCrewById.values()]
  const crewCount = tripCrewSummary.length

  // Count unique passengers by ID across all legs
  const uniquePaxIds = new Set(
    trip?.legs?.flatMap((leg) => leg.passengers?.map((pax) => pax.id) || []) || []
  )
  const paxCount = uniquePaxIds.size

  // Unique airports across all legs, resolved against the bundled airport db.
  // Computed before the early returns because useAirportsByIcaos is a hook.
  const uniqueTripAirportIcaos: string[] = [
    ...new Set<string>(
      trip?.legs?.flatMap((leg) => [
        leg.departureAirportIcao,
        leg.arrivalAirportIcao,
      ].filter(Boolean)) || []
    ),
  ]
  const tripAirports = useAirportsByIcaos(uniqueTripAirportIcaos)
  const tripAirportsByIcao = new Map(tripAirports.map((a) => [a.icaoCode, a]))

  // Unique aircraft registrations across all legs (aircraft is per-flight)
  const uniqueAircraftRegistrations: string[] = [
    ...new Set<string>(
      trip?.legs?.map((leg: any) => leg.aircraft?.registration).filter((r: any): r is string => !!r) || []
    ),
  ]

  const updateDocuments = useTripUpdateComplianceDocuments(tripId)
  const deleteNote = useNoteDelete(tripId)
  const statusActions = useTripStatusActions({ tripId, legs: trip?.legs })

  const requestRemoveDocument = (documentId: string) => {
    const doc = complianceDocuments.find((d) => d.id === documentId)
    setRemoveTarget({ type: 'document', id: documentId, title: doc?.title || 'this document' })
  }

  const confirmRemove = async () => {
    if (!removeTarget) return
    try {
      if (removeTarget.type === 'document') {
        const remaining = complianceDocuments
          .filter((d) => d.id !== removeTarget.id)
          .map((d) => d.id)
        await updateDocuments.mutateAsync({ tripId, documentIds: remaining })
        toast.success('Document removed from trip')
      } else {
        await deleteNote.mutateAsync({ id: removeTarget.id })
        toast.success('Note removed')
      }
      setRemoveTarget(null)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to remove')
    }
  }

  if (loading || !isAdminLoaded) {
    return (
      <AppLayout>
        {/* Topbar */}
        <TopBarBackIconButton title="Trip Details" showBackButton={true} />

        {/* Trip Card */}
        <Skeleton className="h-44 w-full rounded-2xl" />

        {/* Content Cards */}
        <div className="flex flex-col gap-4">
          {/* Flights Section */}
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Skeleton className="size-5 rounded-md" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-40 w-full rounded-2xl" />
          </div>

          {/* Documents Section */}
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Skeleton className="size-5 rounded-md" />
              <Skeleton className="h-4 w-28" />
            </div>
            <Card className="flex flex-col gap-2 p-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-1 py-2">
                  <Skeleton className="size-6 rounded-md shrink-0" />
                  <Skeleton className="h-4 w-40" />
                </div>
              ))}
            </Card>
          </div>

          {/* Notes Section */}
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Skeleton className="size-5 rounded-md" />
              <Skeleton className="h-4 w-20" />
            </div>
            <Card className="flex flex-col gap-2 p-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-1 py-2">
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ))}
            </Card>
          </div>
        </div>
      </AppLayout>
    )
  }

  if (!trip) {
    return (
      <div className="min-h-screen bg-background-brand-secondary-bold flex flex-col items-center justify-center">
        <p className="text-white/80 mb-4">Trip not found</p>
        <button
          onClick={() => navigate({ to: '..' })}
          className="text-white"
        >
          Go Back
        </button>
      </div>
    )
  }

  const startDate = getTripStartDate(trip.legs)
  const endDate = getTripEndDate(trip.legs)
  const tripIsEditable = !readOnly && isTripEditable(trip.tripState)
  const canEditNotesAndDocs = trip.tripState !== 'Complete'
  // Documents can be added by admins, or by anyone when allowDocumentUpload is set
  // (crew-facing schedule route). Backend permits any company member to attach docs
  // to a non-complete trip, so the only hard requirement is the trip isn't Complete.
  const canAddDocuments = canEditNotesAndDocs && (isAdmin || allowDocumentUpload)
  // Admins get the full "Add Documents" menu (upload or attach from the compliance
  // library); non-admins go straight to the upload form.
  const handleAddDocument = () => {
    hapticLight()
    if (isAdmin) {
      setIsTripAddDocumentDrawerOpen(true)
    } else {
      setIsTripDocumentDrawerOpen(true)
    }
  }
  const tripStatus = toLifecycleStatus(trip.tripState)
  const isCollapsibleMode = tripStatus === 'complete'

  // Count unique airports across all legs
  const uniqueAirportIcaos = new Set(
    trip.legs?.flatMap((leg) => [
      leg.departureAirportIcao,
      leg.arrivalAirportIcao,
    ].filter(Boolean)) || []
  )
  const airportsCount = uniqueAirportIcaos.size

  // Pilot ring card: flight-log completion across the trip's legs. Hidden while
  // the trip is still a draft (nothing to action until it's scheduled), and
  // once every log is complete (or there are no legs). Legs arrive sorted by
  // departure time.
  const legs = trip.legs ?? []
  const completedLogCount = legs.filter(
    (leg: any) => leg.flightLog?.lifecycle === 'complete'
  ).length
  // A log is owed only once the leg has departed (the log is transferred from
  // the paper folio after the flight). Future legs don't count as incomplete.
  // Only legs this pilot is crewed on count — other pilots' logs are not
  // theirs to complete (the log drawer is read-only for them).
  const owesFlightLog = (leg: any) =>
    leg.flightLog?.lifecycle !== 'complete' &&
    isFlightLogOpenForLeg(leg) &&
    !!currentCrew &&
    (leg.crew ?? []).some((c: { id: string }) => c.id === currentCrew.id)
  const firstIncompleteLegIndex = legs.findIndex(owesFlightLog)
  const showFlightLogRing =
    isPilot &&
    tripStatus !== 'draft' &&
    legs.length > 0 &&
    firstIncompleteLegIndex !== -1
  const incompleteLogCount = legs.filter(owesFlightLog).length
  // Rows for the attention drawer: this trip's incomplete legs, shaped like
  // getMyPendingLogs items so the drawer renders them as-is. Derived from the
  // same predicate as the ring so the drawer always matches its count.
  const pendingTripLogs: PendingFlightLog[] = legs
    .filter(owesFlightLog)
    .map((leg: any) => ({
      id: leg.flightLog?.id ?? `leg:${leg.id}`,
      tripLegId: leg.id,
      tripId,
      logNumber: leg.flightLog?.logNumber ?? null,
      lifecycle: leg.flightLog?.lifecycle ?? 'notStarted',
      departureAirportIcao: leg.departureAirportIcao,
      arrivalAirportIcao: leg.arrivalAirportIcao,
      departureDateTime: leg.departureDateTime,
      aircraftRegistration: leg.aircraft?.registration ?? null,
      defectDeferred: leg.flightLog?.defectDeferred ?? false,
    }))
  const flightLogRingCard = showFlightLogRing ? (
    <div className="mt-4">
      <FlightLogRingCard
        completed={completedLogCount}
        total={legs.length}
        subtitle={`${incompleteLogCount} ${incompleteLogCount === 1 ? 'leg needs' : 'legs need'} attention.`}
        onClick={() => {
          hapticLight()
          setIsFlightLogsAttentionOpen(true)
        }}
      />
    </div>
  ) : null

  const flightsSection = (
    <div className="mt-4">
      <SectionTitle
        label="Flights"
        count={trip.legs?.length || 0}
        onAdd={
          isAdmin && tripIsEditable
            ? () => {
                hapticLight()
                setSelectedFlightId(null)
                setIsFlightDrawerOpen(true)
                setLegSnap(0.6)
              }
            : undefined
        }
        icon={<PlaneTakeoff className="size-5 text-content-brand-bold" />}
      />

      {/* Flight List */}
      {trip.legs && trip.legs.length > 0 ? (
        <TripFlightCarousel
          legs={trip.legs}
          layout={isDesktop ? 'list' : 'carousel'}
          showFlightLog={tripStatus !== 'draft'}
          initialFlightId={openLegId}
          onFlightClick={(flightId) => {
            setSelectedFlightId(flightId)
            setIsFlightDrawerOpen(true)
            setShowMap(false)
            setLegSnap(0.9)
            navigate({
              to: '.',
              search: ((prev: Record<string, unknown>) => ({ ...prev, legId: flightId })) as any,
            })
          }}
        />
      ) : (
        <EmptyStateCard
          label={isAdmin && tripIsEditable ? 'Add a flight' : 'No flights'}
          onAdd={isAdmin && tripIsEditable ? () => {
            hapticLight()
            setSelectedFlightId(null)
            setIsFlightDrawerOpen(true)
            setLegSnap(0.6)
          } : undefined}
        />
      )}
    </div>
  )

  const airportsSection = (
    <div className="mt-4">
      <SectionTitle
        label="Airports"
        count={airportsCount}
        icon={<TowerControl className="size-5 text-content-brand-bold" />}
      />
      {uniqueTripAirportIcaos.length === 0 ? (
        <EmptyStateCard label="Add a flight to see airports" />
      ) : (
      <Card className="overflow-hidden px-3 py-1">
        <PrntrGroup>
          {(showAllAirports ? uniqueTripAirportIcaos : uniqueTripAirportIcaos.slice(0, 3)).map((icao) => {
            const airport = tripAirportsByIcao.get(icao)
            return (
              <PrntrInput
                key={icao}
                label={<span className="text-content-primary-bold">{icao}</span>}
                onClick={() => {
                  hapticLight()
                  setInfoIcao(icao)
                }}
              >
                <PrntrValue value={airport?.name ?? null} placeholder="—" />
              </PrntrInput>
            )
          })}
        </PrntrGroup>
        {uniqueTripAirportIcaos.length > 3 && (
          <button
            type="button"
            className="flex items-center px-1 py-2 text-xs font-semibold text-content-brand-bold active:opacity-70"
            onClick={() => setShowAllAirports((v) => !v)}
          >
            {showAllAirports ? 'Show less' : `Show ${uniqueTripAirportIcaos.length - 3} more`}
          </button>
        )}
      </Card>
      )}
    </div>
  )

  const documentsSection = (
    <div className="mt-4">
      <SectionTitle
        label="Documents"
        count={complianceDocuments.length}
        onAdd={canAddDocuments ? handleAddDocument : undefined}
        icon={<FileText className='size-5 text-content-brand-bold' />}
      />
      {complianceDocuments.length > 0 ? (
        <Card className="flex flex-col gap-2 overflow-hidden p-3">
          {complianceDocuments.map((doc, index) => {
            const rowId = `doc-${doc.id}`
            return (
              <Fragment key={doc.id}>
                {index > 0 && <hr className="border-border-L1" />}
                <SwipeableRow
                  isOpen={openRowId === rowId}
                  onOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                  onDelete={() => {
                    requestRemoveDocument(doc.id)
                    setOpenRowId(null)
                  }}
                  variant="icon"
                  actionLabel="Remove"
                  actionClassName="bg-content-primary-subtle"
                  actionIcon={<CircleMinus />}
                  disabled={!isAdmin || !tripIsEditable}
                >
                  <PrntrInput
                    className="border-b-0 px-1"
                    iconLeft={<File />}
                    label={
                      <span className="flex items-center gap-2 text-content-primary-bold">
                        {doc.title}
                      </span>
                    }
                    onClick={() => {
                      setSelectedDocumentId(doc.id)
                      setIsViewDocumentDrawerOpen(true)
                    }}
                  >
                    <></>
                  </PrntrInput>
                </SwipeableRow>
              </Fragment>
            )
          })}
        </Card>
      ) : (
        <EmptyStateCard
          label={canAddDocuments ? 'Add a document' : 'No documents'}
          onAdd={canAddDocuments ? handleAddDocument : undefined}
        />
      )}
    </div>
  )

  const notesSection = (
    <div className="mt-4">
      <NotesSection
        notes={trip.notes}
        parent={{ tripId }}
        readOnly={!canEditNotesAndDocs}
        collapseAfter={isCollapsibleMode ? 2 : undefined}
        onDeleteRequest={(id, title) =>
          setRemoveTarget({ type: 'note', id, title })
        }
      />
    </div>
  )

  const tripCard = (
    <TripCard
      tripName={trip.tripName}
      startDate={startDate}
      endDate={endDate}
      tripState={trip.tripState}
      flightsCount={trip.legs?.length || 0}
      airportsCount={airportsCount}
      crewCount={crewCount}
      paxCount={paxCount}
      variant="detail"
      statusBadge={
        isAdmin && !readOnly ? (
          <TripStatusSelect
            tripId={tripId}
            tripState={trip.tripState}
            onRequestSchedule={statusActions.requestSchedule}
            onRequestComplete={statusActions.requestComplete}
            isPending={statusActions.isPending}
          />
        ) : undefined
      }
    />
  )

  const detailDialogs = (
    <>
      {/* Remove Document/Note Confirmation Dialog */}
      <AlertDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null)
        }}
      >
        <AlertDialogContent className="p-8 gap-0 text-center">
          <AlertDialogTitle className="text-xl font-semibold text-center leading-[1.2] tracking-[-0.4px]">
            Remove {removeTarget?.type === 'note' ? 'Note' : 'Document'}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-content-primary-subtle text-center mt-4">
            Are you sure you want to remove {removeTarget?.title ? `"${removeTarget.title}"` : 'this item'}?
          </AlertDialogDescription>
          <div className="flex flex-col gap-2 mt-4 w-full">
            <Button
              variant="destructive"
              size="mini"
              className="w-full"
              onClick={confirmRemove}
              disabled={updateDocuments.isPending || deleteNote.isPending}
            >
              {updateDocuments.isPending || deleteNote.isPending ? 'Removing...' : 'Remove'}
            </Button>
            <Button
              variant="secondary"
              size="mini"
              className="w-full"
              onClick={() => setRemoveTarget(null)}
              disabled={updateDocuments.isPending || deleteNote.isPending}
            >
              Cancel
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Schedule / Complete confirmation dialogs (shared with the actions menu) */}
      {statusActions.dialogs}

      {/* Drawers */}
      <AirportDetailDrawer
        icao={infoIcao}
        isOpen={infoIcao != null}
        onClose={() => setInfoIcao(null)}
      />
      <ViewPasswordsDrawer
        isOpen={isViewPasswordsDrawerOpen}
        onClose={() => setIsViewPasswordsDrawerOpen(false)}
        aircraftTails={uniqueAircraftRegistrations}
      />

      <FlightLogsAttentionDrawer
        open={isFlightLogsAttentionOpen}
        onOpenChange={setIsFlightLogsAttentionOpen}
        logs={pendingTripLogs}
      />

      <TripAddDocumentDrawer
        isOpen={isTripAddDocumentDrawerOpen}
        onClose={() => setIsTripAddDocumentDrawerOpen(false)}
        onSelectDocuments={() => setIsAddComplianceDocumentDrawerOpen(true)}
        onSelectUpload={() => setIsTripDocumentDrawerOpen(true)}
      />

      <AddComplianceDocumentToTripDrawer
        isOpen={isAddComplianceDocumentDrawerOpen}
        onClose={() => setIsAddComplianceDocumentDrawerOpen(false)}
        tripId={tripId}
      />

      <DocumentFormDrawer
        isOpen={isTripDocumentDrawerOpen}
        onClose={() => setIsTripDocumentDrawerOpen(false)}
        owner={{ type: 'trip', tripId }}
      />

      {/* The id is kept on close so the drawer can play its exit animation;
          the next tap overwrites it. */}
      <ViewDocumentDrawer
        isOpen={isViewDocumentDrawerOpen}
        onClose={() => setIsViewDocumentDrawerOpen(false)}
        documentId={selectedDocumentId}
        readOnly={true}
        onRemove={isAdmin && tripIsEditable && selectedDocumentId ? async () => {
          requestRemoveDocument(selectedDocumentId)
        } : undefined}
      />
    </>
  )

  // Trip leg drawer — shared between layouts. On desktop, FlightDrawer renders
  // as a centered modal and ignores the snap/map props.
  const legDrawer = (
    <FlightDrawer
      isOpen={isFlightDrawerOpen}
      onClose={() => {
        setIsFlightDrawerOpen(false)
        setSelectedFlightId(null)
        setShowMap(false)
        navigate({
          to: '.',
          search: ((prev: Record<string, unknown>) => ({ ...prev, legId: undefined })) as any,
          replace: true,
        })
      }}
      tripId={tripId}
      tripLegId={selectedFlightId}
      mode={selectedFlightId ? 'edit' : 'create'}
      readOnly={!tripIsEditable}
      snapPoints={[0.3, 0.5, 0.6, 0.9]}
      activeSnapPoint={legSnap}
      setActiveSnapPoint={setLegSnap}
      isMapVisible={showMap}
      onToggleMap={() => {
        const next = !showMap
        setShowMap(next)
        // Collapse to a peek so the map is visible; restore near-full when off.
        setLegSnap(next ? 0.3 : 0.9)
      }}
    />
  )

  if (isDesktop) {
    return (
      <>
        <DesktopPage>
          <DesktopDetailHeader
            title={trip.tripName}
            onBack={handleBack}
            breadcrumb={
              <span>
                <Link to="/operations" className="hover:underline">
                  Operations
                </Link>
                <span className="text-content-primary-subtle"> / </span>
                <Link to="/operations/trips" className="hover:underline">
                  Trips
                </Link>
                <span className="text-content-primary-subtle">
                  {' '}/ {trip.tripName}
                </span>
              </span>
            }
            action={
              isAdmin && tripIsEditable ? (
                <TripActionsSelect tripId={tripId} />
              ) : undefined
            }
          />

          {tripCard}

          {isAdmin && readOnly && (
            <Card className="flex items-center justify-between p-4 border-border-L2 bg-surface-L2">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-content-primary-bold">Manage this trip?</p>
                <p className="text-xs text-content-primary-subtle">To manage this trip, go to Trips under Operations.</p>
              </div>
              <Button
                variant="primary"
                size="sm"
                roundness="round"
                onClick={() => navigate({ to: '/operations/trips/$tripId', params: { tripId } })}
              >
                Manage
              </Button>
            </Card>
          )}

          {flightLogRingCard}

          {flightsSection}

          <div className="grid grid-cols-2 gap-4">
            {documentsSection}
            {notesSection}
          </div>

          {airportsSection}
        </DesktopPage>

        {detailDialogs}
        {legDrawer}
      </>
    )
  }

  return (
    <>
    <AppLayout wrapperClassName={showMap ? 'hidden' : undefined}>
      {/* Topbar */}
      <TopBarBackIconButton
        title={trip.tripName}
        rightElement={isAdmin && tripIsEditable ? (
          <TripActionsSelect tripId={tripId} />
        ) : undefined}
        showBackButton={true}
      />

      {/* Trip Card */}
      {tripCard}

      {/* Manage Trip Card (admin viewing from Schedule) */}
      {isAdmin && readOnly && (
        <Card className="flex items-center justify-between p-4 mt-4 border-border-L2 bg-surface-L2">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold text-content-primary-bold">Manage this trip?</p>
            <p className="text-xs text-content-primary-subtle">To manage this trip, go to Trips under Operations.</p>
          </div>
          <Button
            variant="primary"
            size="sm"
            roundness="round"
            onClick={() => navigate({ to: '/operations/trips/$tripId', params: { tripId } })}
          >
            Manage
          </Button>
        </Card>
      )}

      {/* Content Cards */}
      <div className="flex flex-col gap-4">
        {flightLogRingCard}
        {flightsSection}
        {documentsSection}
        {notesSection}
        {airportsSection}
      <div className="h-8"/>

      </div>

      {detailDialogs}
    </AppLayout>

      {/* Flight map backdrop — toggled by the drawer's globe button. Rendered
          outside AppLayout (which is hidden while the map shows) so the native
          map behind the transparent webview is visible. */}
      {showMap && Capacitor.isNativePlatform() && selectedLeg && mapDeparture && mapArrival && (
        <div className="fixed inset-0 z-40">
          <FlightMap
            showModeToggle
            departure={mapDeparture}
            arrival={mapArrival}
            departureDateTime={selectedLeg.departureDateTime}
            arrivalDateTime={selectedLeg.arrivalDateTime}
            cameraAltitude={50_000_000}
            edgePadding={{
              top: 96,
              left: 40,
              bottom: Math.round(window.innerHeight * 0.38),
              right: 40,
            }}
          />
        </div>
      )}

      {legDrawer}
    </>
  )
}
